import { ElementType, MouseEventHandler, ReactNode, SyntheticEvent, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { flatten } from 'lodash';
import classNames from 'classnames';
import DoneIcon from '@mui/icons-material/Done';
import BootstrapDropdown from 'react-bootstrap/Dropdown';

import { createTestId, DynamicTestId } from 'common/element';
import { MenuItemConfig } from 'common/types';
import { normalizeLink } from 'common/util/location';
import { permissionRecordState } from 'components/account/account.state';
import Link from 'components/common/Link/Link';

import ElementWithTooltip from '../ElementWithTooltip/ElementWithTooltip';
import SubMenuTrigger from './SubMenuTrigger';

import './DropdownItems.scss';

const doneIcon = <DoneIcon fontSize="small" />;

/**
 * IconTextPair simply encapsulates a common menu pattern of a child that's an icon and text.
 *
 * @prop label        The text
 * @prop leftIcon     The icon
 */
type IconTextPairProps = {
  label: string;
  leftIcon: ReactNode;
};

export function IconTextPair({ label, leftIcon }: IconTextPairProps): JSX.Element {
  return (
    <>
      {leftIcon}
      {` ${label}`}
    </>
  );
}

function getMenuItem(
  item: MenuItemConfig | null,
  closeBefore: (fn?: MouseEventHandler) => MouseEventHandler,
  menuItemIndex: number,
  key: string,
  itemAs?: ElementType,
  itemClass?: string,
  openInNewTab?: boolean,
  testId?: DynamicTestId,
): JSX.Element {
  if (!item) {
    return (
      <BootstrapDropdown.Divider
        className="DropdownItems__Divider"
        key={key}
        data-item-index={menuItemIndex}
        data-test-id={createTestId(testId, 'divider', menuItemIndex)}
      />
    );
  }

  let as = item.as || itemAs;
  if (!as && !item.href && !item.clickHandler) {
    as = 'span';
  }
  const onClick = item.disabled ? undefined : closeBefore(item.clickHandler);
  const onEnter = item.disabled ? undefined : item.enterHandler;
  const onLeave = item.disabled ? undefined : item.leaveHandler;
  const done = item.active ? doneIcon : null;
  const newTab = openInNewTab || item.openInNewTab;

  // make sure a local target URL is relative; if it isn't, Bootstrap prepends '/'
  const href = normalizeLink(item.href || '');

  const iconsTestId = item.testId || (testId ? createTestId(testId, 'item', 'icons', menuItemIndex) : undefined);

  let hoverIcons = null;
  if (item.hoverActions) {
    hoverIcons = (
      <div className="DropdownItems__HoverIcons" data-test-id={iconsTestId}>
        {item.hoverActions.map((hoverAction, hoverActionIndex) => {
          const hoverHandler = (event: SyntheticEvent, menuItem: MenuItemConfig): void => {
            hoverAction.action(menuItem);
            event.preventDefault();
            event.stopPropagation();
          };
          return (
            <span
              onClick={(ev: SyntheticEvent): void => hoverHandler(ev, item)}
              className="DropdownItems__RightIcon"
              role="button"
              tabIndex={0}
              key={createTestId(key, hoverActionIndex)}
              data-test-id={hoverAction.testId ? createTestId(hoverAction.testId, key) : undefined}
            >
              {hoverAction.icon}
            </span>
          );
        })}
      </div>
    );
  }

  let itemContent = null;

  if ((item.subMenuItems ?? []).length > 0) {
    itemContent = (
      <SubMenuTrigger item={item} closeBefore={closeBefore} done={done} itemKey={`${key}-submenu-${menuItemIndex}`} />
    );
  } else {
    itemContent = (
      <>
        <span style={{ width: '100%' }} className={item.className || itemClass}>
          {done}
          {item.children || item.text}
        </span>
        {hoverIcons}
      </>
    );
  }

  const className = classNames('DropdownItems__Item', {
    focus: item.focused,
    actionable: !!(item.href || item.clickHandler),
    'Item--withSubMenu': (item.subMenuItems ?? []).length > 0,
  });

  // test ID provided in 'data-test-id' for BS item, and in 'testId' for rendering as a Link
  const itemTestId = item.testId || (testId ? createTestId(testId, 'item', key) : undefined);
  const itemProps =
    as === Link ? { testId: itemTestId, data: { 'data-item-index': menuItemIndex } } : { 'data-test-id': itemTestId };

  // We use a <span/> wrapper here to handle onClick,
  //  as <BootstrapDropdown.Item/> doesn't call onClick handler when `as` and `href` props are defined
  return (
    <ElementWithTooltip placement="auto" tooltip={item.disabled ? item.disabledTooltip : item.tooltip} key={key}>
      <span role="navigation" onClick={onClick} onMouseEnter={onEnter} onMouseLeave={onLeave}>
        <BootstrapDropdown.Item
          as={as as 'symbol' | undefined}
          href={href || undefined}
          className={className}
          active={!!item.active}
          target={newTab ? '_blank' : undefined}
          disabled={item.disabled}
          data-item-index={menuItemIndex}
          {...itemProps}
        >
          {itemContent}
        </BootstrapDropdown.Item>
      </span>
    </ElementWithTooltip>
  );
}

/**
 * DropdownItems renders a list of menu items. The Dropdown component uses DropdownItems,
 * but they can be composed with a custom parent such as SearchHelper as well.
 * Each item is rendered as a BootstrapDropdown.Item, or a BootstrapDropdown.Divider
 * if the item is falsy (rather than an item configuration object).
 *
 * The caller must provide a list of item sections. Each section comprises
 * a list of item configurations. Sections are a convenient way to get around the problem
 * of conditionally adding dividers based on items that may or may not be present. Any two
 * sections that have at least one item to render will be separated by a divider.
 *
 * @prop sections        list of sections where each section is a list of MenuItemConfig
 * @prop itemAs          element or component to use to render each item
 * @prop itemClass       CSS class for each item
 * @prop openInNewTab    if true, open link targets in new tab
 * @prop close           function to call when closing the menu
 * @prop testId          base test ID for items that lack their own testId
 */
type DropdownItemsProps = {
  sections: Array<Array<MenuItemConfig>>;
  itemAs?: ElementType;
  itemClass?: string;
  openInNewTab?: boolean;
  close?: () => void;
  selectionText?: string;
  testId?: DynamicTestId;
};

function DropdownItems({
  sections,
  itemAs,
  itemClass,
  openInNewTab = false,
  close,
  selectionText,
  testId,
}: DropdownItemsProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const permissionRecord = useRecoilValue(permissionRecordState);

  // first, remove items from each section for which the user lacks permissions
  const allowedSections = sections.map((section) =>
    section.filter((item) => !item.permission || permissionRecord[item.permission]),
  );
  // next, remove empty sections, join the remaining with dividers, and then flatten into items
  const populatedSections = allowedSections.filter((section) => section.length > 0);
  const items = flatten(
    populatedSections
      .map((e, i) => (i < populatedSections.length - 1 ? [e, null] : [e]))
      .reduce((a, b) => a.concat(b), []),
  );

  if (selectionText) {
    const focusIndex = items.findIndex(
      (item) => item && item.text && item.text.toLowerCase().startsWith(selectionText.toLowerCase()),
    );
    items.forEach((item, index) => {
      if (item) {
        if (containerRef.current && index === focusIndex) {
          const itemEl = containerRef.current.querySelector('[data-item-index="' + focusIndex + '"]');
          if (itemEl) {
            (itemEl as HTMLElement).focus();
          }
        }
      }
    });
  }

  // call close method if one has been provided; same for an item-specific click handler
  const closeBefore =
    (callback?: MouseEventHandler): MouseEventHandler =>
    (ev): void => {
      if (close) {
        close();
      }
      if (callback) {
        callback(ev);
      }
    };

  // Convert each item config into a dropdown item or a divider. The content is wrapped in a DIV so that we
  // can add a click handler (BootstrapDropdown.Item eats the click event for anchor tags).
  const menuItems = items.map((item, index) =>
    getMenuItem(
      item,
      closeBefore,
      index,
      item ? item.key || `item-${index}` : `divider-${index}`,
      itemAs,
      itemClass,
      openInNewTab,
      testId,
    ),
  );

  return (
    <div data-test-id={testId} ref={containerRef}>
      {menuItems}
    </div>
  );
}

export default DropdownItems;
export { getMenuItem };
